import React, { useState, useEffect, useRef } from 'react';
import { Play, Heart, Share2, Eye, Calendar, ArrowUp, ChevronLeft, ChevronRight, VolumeX, Volume2, X, Clock, AlertCircle } from 'lucide-react';
import { 
  fetchAllVideoGalleries, 
  getVideoThumbnail,
  getEmbedUrl,
  formatMarathiDate
} from '../apiService';
import { motion } from 'framer-motion';
import { useLanguage } from '../utils/LanguageContext';
import Footer from '../components/footer';

const VideoAlbum = () => {
  const [videoGalleries, setVideoGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [likedGalleries, setLikedGalleries] = useState(new Set());
  const [playbackError, setPlaybackError] = useState(false);
  const iframeRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchAllVideoGalleries(1, 12);
        
        if (result && Array.isArray(result.videoGalleries)) {
          setVideoGalleries(result.videoGalleries);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || (language === 'MR' ? 'व्हिडिओ लोड करताना त्रुटी आली' : 'Error loading videos'));
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [language]);

  const openVideoModal = (gallery, video) => {
    if (!video || !video.videoUrl) {
      setError(language === 'MR' ? 'व्हिडिओ URL उपलब्ध नाही' : 'Video URL not available');
      return;
    }

    setSelectedGallery(gallery);
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
    setPlaybackError(false);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedGallery(null);
    setSelectedVideo(null);
    setPlaybackError(false);
    document.body.style.overflow = 'unset';
  };

  const navigateVideo = (direction) => {
    if (!selectedGallery || !selectedVideo || !selectedGallery.videos) return;
    
    const currentIndex = selectedGallery.videos.findIndex(v => v._id === selectedVideo._id);
    if (currentIndex === -1) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = currentIndex < selectedGallery.videos.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : selectedGallery.videos.length - 1;
    }
    
    const newVideo = selectedGallery.videos[newIndex];
    if (!newVideo.videoUrl) {
      setPlaybackError(true);
      return;
    }
    
    setSelectedVideo(newVideo);
    setPlaybackError(false);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleLike = (galleryId, e) => {
    e.stopPropagation();
    setLikedGalleries((prev) => {
      const newSet = new Set(prev);
      newSet.has(galleryId) ? newSet.delete(galleryId) : newSet.add(galleryId);
      return newSet;
    });
  };

  const handleShare = async (gallery, e) => {
    e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({
          title: gallery.title,
          text: gallery.description || (language === 'MR' ? "हे व्हिडिओ अल्बम पहा" : "Check out this video album"),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert(language === 'MR' ? "लिंक कॉपी केला गेला!" : "Link copied!");
      }
    } catch (error) {
      console.log("Sharing failed:", error);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'MR' ? 'mr-IN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTotalVideos = () => {
    return videoGalleries.reduce((total, gallery) => total + (gallery.videos?.length || 0), 0);
  };

  const handleVideoError = () => {
    setPlaybackError(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-red-700 font-semibold text-lg font-marathi">
            {language === 'MR' ? 'व्हिडिओ अल्बम लोड होत आहे...' : 'Loading video album...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-2 font-marathi">
            {language === 'MR' ? 'त्रुटी आली' : 'Error Occurred'}
          </h2>
          <p className="text-gray-600 mb-4 font-marathi">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-marathi"
          >
            {language === 'MR' ? 'पुन्हा प्रयत्न करा' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-red-800 font-marathi">
            {language === 'MR' ? 'चलचित्रे क्षणचित्रे' : 'Mirrors of Scenes'}
          </h1>
          <p className="text-gray-600 max-w-3xl mx-auto text-xl leading-relaxed font-marathi mb-8">
            {language === 'MR' ? 'आमचे सर्वोत्तम व्हिडिओ अन्वेषण करा' : 'Explore our collection of best videos'}
          </p>
          <div className="flex justify-center items-center gap-4 text-gray-500">
            <span className="flex items-center gap-1">
              <Play className="w-4 h-4" />
              {videoGalleries.length} {language === 'MR' ? 'अल्बम' : 'Albums'}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {getTotalVideos()} {language === 'MR' ? 'व्हिडिओ' : 'Videos'}
            </span>
          </div>
        </motion.div>

        {/* Subtitle Section */}
        <motion.div 
          className="mb-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-black mb-2 font-marathi">
            {videoGalleries.length > 0
              ? language === 'MR' 
                ? `माझ्या दृष्टिकोनाची झलक` 
                : `My Video Collections`
              : language === 'MR' 
                ? "अल्बम उपलब्ध नाहीत" 
                : "No albums available"}
          </h2>
        </motion.div>

        {/* Video Gallery Grid */}
        {videoGalleries.length === 0 ? (
          <div className="text-center py-20">
            <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-marathi">
              {language === 'MR' ? 'अद्याप कोणतेही व्हिडिओ अल्बम उपलब्ध नाही' : 'No video albums available yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videoGalleries.map((gallery, index) => (
              <motion.div
                key={gallery._id || index}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer"
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                onClick={() => gallery.videos?.[0] && openVideoModal(gallery, gallery.videos[0])}
              >
                {/* Video Thumbnail Container */}
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                  {gallery.videos?.[0] ? (
                    <>
                      <motion.img
                        src={getVideoThumbnail(gallery.videos[0])}
                        alt={gallery.title || "Video Album"}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                      />
                      
                      {/* Video Count Badge */}
                      <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Play className="w-3 h-3" />
                        {gallery.videos?.length || 0}
                      </div>

                      {/* Duration Badge (if available) */}
                      {gallery.videos[0].duration && (
                        <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {gallery.videos[0].duration}
                        </div>
                      )}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div 
                          className="w-16 h-16 bg-red-500/80 rounded-full flex items-center justify-center transition-opacity"
                          animate={{ 
                            opacity: hovered === index ? 1 : 0.7,
                            scale: hovered === index ? 1.1 : 1 
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          <Play className="w-8 h-8 text-white ml-1" />
                        </motion.div>
                      </div>

                      {/* Hover Overlay with Actions */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-end p-3">
                        <div className="flex gap-2">
                          <motion.button
                            onClick={(e) => handleLike(gallery._id, e)}
                            className={`p-2 rounded-full backdrop-blur-sm transition-all duration-200 ${
                              likedGalleries.has(gallery._id)
                                ? "bg-red-500 text-white"
                                : "bg-white/20 text-white hover:bg-white/30"
                            }`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Heart
                              className="w-4 h-4"
                              fill={likedGalleries.has(gallery._id) ? "currentColor" : "none"}
                            />
                          </motion.button>
                          <motion.button
                            onClick={(e) => handleShare(gallery, e)}
                            className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm transition-all duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Share2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <Play className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-lg mb-2 font-marathi line-clamp-1">
                    {gallery.title || (language === 'MR' ? 'नाव नसलेले अल्बम' : 'Untitled Album')}
                  </h3>
                  
                  {gallery.description && (
                    <p className="text-gray-600 text-sm mb-3 font-marathi line-clamp-2">
                      {gallery.description}
                    </p>
                  )}

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(gallery.createdAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {gallery.views || 0}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 p-3 text-red-600 hover:text-red-800 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-red-200 z-50 flex items-center justify-center"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title={language === 'MR' ? 'वर जा' : 'Go up'}
        aria-label="Scroll to top"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1 }}
      >
        <ArrowUp className="w-5 h-5" />
      </motion.button>

      {/* Video Modal */}
      {isVideoModalOpen && selectedVideo && selectedGallery && (
        <motion.div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button 
            onClick={closeVideoModal}
            className="absolute top-4 right-4 text-white hover:text-red-400 transition-colors z-50"
          >
            <X className="w-8 h-8" />
          </button>
          
          <motion.div 
            className="relative w-full max-w-6xl aspect-video bg-black rounded-xl overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {playbackError ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 p-6">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2 font-marathi">
                  {language === 'MR' ? 'व्हिडिओ प्ले करण्यात त्रुटी' : 'Video playback error'}
                </h3>
                <p className="text-gray-300 mb-4 text-center font-marathi">
                  {language === 'MR' 
                    ? 'क्षमस्व, हा व्हिडिओ प्ले होऊ शकत नाही. कृपया नंतर पुन्हा प्रयत्न करा.' 
                    : 'Sorry, this video cannot be played. Please try again later.'}
                </p>
                <button
                  onClick={() => setPlaybackError(false)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-marathi"
                >
                  {language === 'MR' ? 'पुन्हा प्रयत्न करा' : 'Try Again'}
                </button>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                src={`${getEmbedUrl(selectedVideo)}?autoplay=1&mute=${isMuted ? 1 : 0}&rel=0&modestbranding=1`}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedVideo.title}
                onError={handleVideoError}
                sandbox="allow-same-origin allow-scripts allow-popups"
              />
            )}
            
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
              <div className="flex justify-between items-end">
                <div>
                  <h3 className="text-xl font-bold text-white font-marathi">{selectedVideo.title}</h3>
                  <p className="text-red-200 font-marathi">{selectedGallery.title}</p>
                </div>
                
                <div className="flex items-center gap-4">
                  <button 
                    onClick={toggleMute}
                    className="text-white hover:text-red-400 transition-colors"
                  >
                    {isMuted ? (
                      <VolumeX className="w-6 h-6" />
                    ) : (
                      <Volume2 className="w-6 h-6" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {selectedGallery.videos?.length > 1 && (
              <>
                <button 
                  onClick={() => navigateVideo('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-red-500 transition-colors z-10"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                
                <button 
                  onClick={() => navigateVideo('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-red-500 transition-colors z-10"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}

      <style jsx global>{`
        @font-face {
          font-family: 'Marathi';
          src: url('/fonts/NotoSansDevanagari-Regular.ttf') format('truetype');
          font-display: swap;
        }
        .font-marathi {
          font-family: 'Marathi', 'Noto Sans Devanagari', sans-serif;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      <Footer />
    </div>
  );
};

export default VideoAlbum;